/**
 * React Query hooks around the unified `/api/admin/paint-catalog`
 * router. Used by `CatalogAdmin.tsx` to load and mutate the paint
 * catalog regardless of kind.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  api,
  PaintAsset,
  PaintAvailability,
  PaintKind,
  PaintMode,
  PaintSet,
  PaintSetEntry,
  TaxonomyNode,
  TaxonomyNodePaintSet,
} from "@west-marches/shared";
import { apiFetch } from "@/lib/apiClient";

type AttachChildPaintSetToPaintSetInput = api.AttachChildPaintSetToPaintSetInput;
type AttachPaintAssetToPaintSetInput = api.AttachPaintAssetToPaintSetInput;
type AttachPaintSetToFolderInput = api.AttachPaintSetToFolderInput;
type DetachPaintSetEntryInput = api.DetachPaintSetEntryInput;
type BulkMovePaintSetsInput = api.BulkMovePaintSetsInput;
type BulkSetPaintSetAvailabilityInput = api.BulkSetPaintSetAvailabilityInput;
type DeletePaintAssetInput = api.DeletePaintAssetInput;
type DeletePaintSetInput = api.DeletePaintSetInput;
type DeletePaintTaxonomyNodeInput = api.DeletePaintTaxonomyNodeInput;
type MergePaintSetsInput = api.MergePaintSetsInput;
type MovePaintAssetBetweenSetsInput = api.MovePaintAssetBetweenSetsInput;
type MovePaintTaxonomyNodeInput = api.MovePaintTaxonomyNodeInput;
type ReorderPaintFolderContentsInput = api.ReorderPaintFolderContentsInput;
type ReorderPaintSetEntriesInput = api.ReorderPaintSetEntriesInput;
type ReorderPaintSetsInFolderInput = api.ReorderPaintSetsInFolderInput;
type ReorderPaintTaxonomyNodesInput = api.ReorderPaintTaxonomyNodesInput;
type SetPaintAssetAvailabilityInput = api.SetPaintAssetAvailabilityInput;
type SetPaintSetAvailabilityInput = api.SetPaintSetAvailabilityInput;
type SetPaintSetPaintModeInput = api.SetPaintSetPaintModeInput;
type SetPaintTaxonomyNodeAvailabilityInput = api.SetPaintTaxonomyNodeAvailabilityInput;
type SplitPaintSetInput = api.SplitPaintSetInput;
type UpsertPaintAssetInput = api.UpsertPaintAssetInput;
type UpsertPaintSetInput = api.UpsertPaintSetInput;
type UpsertPaintTaxonomyNodeInput = api.UpsertPaintTaxonomyNodeInput;

export interface PaintCatalogSnapshot {
  taxonomyNodes: TaxonomyNode[];
  paintSets: PaintSet[];
  paintAssets: PaintAsset[];
  paintSetEntries: PaintSetEntry[];
  taxonomyNodePaintSets: TaxonomyNodePaintSet[];
}

const KIND_QUERY_KEY = "admin-paint-catalog";

async function jsonRequest<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await apiFetch(path, init);
  if (!response.ok) {
    const text = await response.text();
    throw Object.assign(new Error(text || `Request failed (${response.status})`), {
      status: response.status,
    });
  }
  return (await response.json()) as T;
}

export function usePaintCatalog(kind: PaintKind) {
  return useQuery<PaintCatalogSnapshot>({
    queryKey: [KIND_QUERY_KEY, kind],
    queryFn: () => jsonRequest<PaintCatalogSnapshot>(`/api/admin/paint-catalog?kind=${kind}`),
    staleTime: 5_000,
  });
}

function useInvalidateKind(kind: PaintKind): () => void {
  const client = useQueryClient();
  return () => {
    void client.invalidateQueries({ queryKey: [KIND_QUERY_KEY, kind] });
  };
}

function postJson<TInput, TOutput>(path: string) {
  return async (input: TInput): Promise<TOutput> =>
    jsonRequest<TOutput>(path, {
      method: "POST",
      body: JSON.stringify(input),
    });
}

export function useUpsertTaxonomyNode(kind: PaintKind) {
  const invalidate = useInvalidateKind(kind);
  return useMutation({
    mutationFn: postJson<UpsertPaintTaxonomyNodeInput, { node: TaxonomyNode }>(
      "/api/admin/paint-catalog/taxonomy-node",
    ),
    onSuccess: invalidate,
  });
}

export function useMoveTaxonomyNode(kind: PaintKind) {
  const invalidate = useInvalidateKind(kind);
  return useMutation({
    mutationFn: postJson<MovePaintTaxonomyNodeInput, { node: TaxonomyNode }>(
      "/api/admin/paint-catalog/taxonomy-node/move",
    ),
    onSuccess: invalidate,
  });
}

export function useReorderTaxonomyNodes(kind: PaintKind) {
  const invalidate = useInvalidateKind(kind);
  return useMutation({
    mutationFn: postJson<ReorderPaintTaxonomyNodesInput, { success: true }>(
      "/api/admin/paint-catalog/taxonomy-node/reorder",
    ),
    onSuccess: invalidate,
  });
}

export function useReorderPaintFolderContents(kind: PaintKind) {
  const invalidate = useInvalidateKind(kind);
  return useMutation({
    mutationFn: postJson<ReorderPaintFolderContentsInput, { success: true }>(
      "/api/admin/paint-catalog/taxonomy-node/contents/reorder",
    ),
    onSuccess: invalidate,
  });
}

export function useSetTaxonomyNodeAvailability(kind: PaintKind) {
  const invalidate = useInvalidateKind(kind);
  return useMutation({
    mutationFn: postJson<SetPaintTaxonomyNodeAvailabilityInput, { node: TaxonomyNode }>(
      "/api/admin/paint-catalog/taxonomy-node/availability",
    ),
    onSuccess: invalidate,
  });
}

export function useDeleteTaxonomyNode(kind: PaintKind) {
  const invalidate = useInvalidateKind(kind);
  return useMutation({
    mutationFn: postJson<DeletePaintTaxonomyNodeInput, { success: true }>(
      "/api/admin/paint-catalog/taxonomy-node/delete",
    ),
    onSuccess: invalidate,
  });
}

export function useUpsertPaintSet(kind: PaintKind) {
  const invalidate = useInvalidateKind(kind);
  return useMutation({
    mutationFn: postJson<UpsertPaintSetInput, { paintSet: PaintSet }>(
      "/api/admin/paint-catalog/paint-set",
    ),
    onSuccess: invalidate,
  });
}

export function useSetPaintSetPaintMode(kind: PaintKind) {
  const invalidate = useInvalidateKind(kind);
  return useMutation({
    mutationFn: postJson<SetPaintSetPaintModeInput, { paintSet: PaintSet }>(
      "/api/admin/paint-catalog/paint-set/paint-mode",
    ),
    onSuccess: invalidate,
  });
}

export function useSetPaintSetAvailability(kind: PaintKind) {
  const invalidate = useInvalidateKind(kind);
  return useMutation({
    mutationFn: postJson<SetPaintSetAvailabilityInput, { paintSet: PaintSet }>(
      "/api/admin/paint-catalog/paint-set/availability",
    ),
    onSuccess: invalidate,
  });
}

export function useDeletePaintSet(kind: PaintKind) {
  const invalidate = useInvalidateKind(kind);
  return useMutation({
    mutationFn: postJson<DeletePaintSetInput, { success: true }>(
      "/api/admin/paint-catalog/paint-set/delete",
    ),
    onSuccess: invalidate,
  });
}

export function useAttachPaintSetToFolder(kind: PaintKind) {
  const invalidate = useInvalidateKind(kind);
  return useMutation({
    mutationFn: postJson<AttachPaintSetToFolderInput, { success: true }>(
      "/api/admin/paint-catalog/paint-set/folder/attach",
    ),
    onSuccess: invalidate,
  });
}

export function useReorderPaintSetsInFolder(kind: PaintKind) {
  const invalidate = useInvalidateKind(kind);
  return useMutation({
    mutationFn: postJson<ReorderPaintSetsInFolderInput, { success: true }>(
      "/api/admin/paint-catalog/paint-set/folder/reorder",
    ),
    onSuccess: invalidate,
  });
}

export function useUpsertPaintAsset(kind: PaintKind) {
  const invalidate = useInvalidateKind(kind);
  return useMutation({
    mutationFn: postJson<UpsertPaintAssetInput, { paintAsset: PaintAsset }>(
      "/api/admin/paint-catalog/paint-asset",
    ),
    onSuccess: invalidate,
  });
}

export function useDeletePaintAsset(kind: PaintKind) {
  const invalidate = useInvalidateKind(kind);
  return useMutation({
    mutationFn: postJson<DeletePaintAssetInput, { success: true }>(
      "/api/admin/paint-catalog/paint-asset/delete",
    ),
    onSuccess: invalidate,
  });
}

export function useSetPaintAssetAvailability(kind: PaintKind) {
  const invalidate = useInvalidateKind(kind);
  return useMutation({
    mutationFn: postJson<SetPaintAssetAvailabilityInput, { paintAsset: PaintAsset }>(
      "/api/admin/paint-catalog/paint-asset/availability",
    ),
    onSuccess: invalidate,
  });
}

export function useMovePaintAssetBetweenSets(kind: PaintKind) {
  const invalidate = useInvalidateKind(kind);
  return useMutation({
    mutationFn: postJson<MovePaintAssetBetweenSetsInput, { success: true }>(
      "/api/admin/paint-catalog/paint-asset/move",
    ),
    onSuccess: invalidate,
  });
}

export function useAttachPaintAssetToPaintSet(kind: PaintKind) {
  const invalidate = useInvalidateKind(kind);
  return useMutation({
    mutationFn: postJson<AttachPaintAssetToPaintSetInput, { entry: PaintSetEntry }>(
      "/api/admin/paint-catalog/paint-asset/attach",
    ),
    onSuccess: invalidate,
  });
}

export function useReorderPaintSetEntries(kind: PaintKind) {
  const invalidate = useInvalidateKind(kind);
  return useMutation({
    mutationFn: postJson<ReorderPaintSetEntriesInput, { success: true }>(
      "/api/admin/paint-catalog/paint-set/entries/reorder",
    ),
    onSuccess: invalidate,
  });
}

export function useAttachChildPaintSetToPaintSet(kind: PaintKind) {
  const invalidate = useInvalidateKind(kind);
  return useMutation({
    mutationFn: postJson<AttachChildPaintSetToPaintSetInput, { entry: PaintSetEntry }>(
      "/api/admin/paint-catalog/paint-set/entries/attach-child-set",
    ),
    onSuccess: invalidate,
  });
}

export function useDetachPaintSetEntry(kind: PaintKind) {
  const invalidate = useInvalidateKind(kind);
  return useMutation({
    mutationFn: postJson<DetachPaintSetEntryInput, { success: true }>(
      "/api/admin/paint-catalog/paint-set/entries/detach",
    ),
    onSuccess: invalidate,
  });
}

export function useMergePaintSets(kind: PaintKind) {
  const invalidate = useInvalidateKind(kind);
  return useMutation({
    mutationFn: postJson<MergePaintSetsInput, { paintSet: PaintSet }>(
      "/api/admin/paint-catalog/paint-set/merge",
    ),
    onSuccess: invalidate,
  });
}

export function useSplitPaintSet(kind: PaintKind) {
  const invalidate = useInvalidateKind(kind);
  return useMutation({
    mutationFn: postJson<SplitPaintSetInput, { paintSets: PaintSet[] }>(
      "/api/admin/paint-catalog/paint-set/split",
    ),
    onSuccess: invalidate,
  });
}

export function useBulkMovePaintSets(kind: PaintKind) {
  const invalidate = useInvalidateKind(kind);
  return useMutation({
    mutationFn: postJson<BulkMovePaintSetsInput, { success: true }>(
      "/api/admin/paint-catalog/paint-set/bulk-move",
    ),
    onSuccess: invalidate,
  });
}

export function useBulkSetPaintSetAvailability(kind: PaintKind) {
  const invalidate = useInvalidateKind(kind);
  return useMutation({
    mutationFn: postJson<BulkSetPaintSetAvailabilityInput, { success: true }>(
      "/api/admin/paint-catalog/paint-set/bulk-availability",
    ),
    onSuccess: invalidate,
  });
}

export const PAINT_AVAILABILITY_LABELS: Record<PaintAvailability, string> = {
  available: "Available",
  unavailable: "Unavailable",
  inherit: "Inherit",
};

export const PAINT_MODE_LABELS: Record<PaintMode, string> = {
  ordered: "Ordered",
  dynamic: "Dynamic",
};
